import { ChevronsUpDown, LogOut, User } from 'lucide-react';
import { cloneElement, isValidElement, useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { BrandName, BrandTile } from '@/components/ui/brand-mark';
import { CONFIG } from '@/config-globals';
import { usePathname } from '@/hooks/use-pathname';
import { useRouter } from '@/hooks/use-router';
import { ac } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useIsAuthenticated, usePermissions, useSignOut } from '@/services/auth';
import { Icon } from './icon';
import { ContentLoader } from './loaders';

interface MenuItem {
  title: string;
  url: string;
  icon: React.ReactNode;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

export function AppSidebar() {
  const router = useRouter();
  const pathname = usePathname();
  const { mutate: signOut } = useSignOut();
  const { user } = useIsAuthenticated();
  const { hasPermission } = usePermissions();
  const { toggleSidebar, isMobile } = useSidebar();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(false);
  }, [pathname]);

  if (!user) return null;

  const items: MenuSection[] = [
    {
      title: 'Geral',
      items: [
        {
          title: 'Dashboard',
          url: '/',
          icon: <Icon name="layout-dashboard" className="size-4" />,
        },
      ],
    },
    {
      title: 'Acervo',
      items: [
        {
          title: 'Cifras',
          url: '/songs',
          icon: <Icon name="music" className="size-4" />,
        },
        {
          title: 'Repertórios',
          url: '/setlists',
          icon: <Icon name="list-music" className="size-4" />,
        },
        {
          title: 'Artistas',
          url: '/artists',
          icon: <Icon name="mic-vocal" className="size-4" />,
        },
      ],
    },
    {
      title: 'Configurações',
      items: [
        {
          title: 'Usuários',
          url: '/users',
          icon: <Icon name="users" className="size-4" />,
        },
      ],
    },
  ];

  const handleLogout = () => {
    setIsLoading(true);
    setTimeout(() => {
      signOut();
    }, 500);
  };

  const handlePush = (url: string) => {
    if (pathname !== url) {
      setIsLoading(true);
      router.push(`/${url}`);
    }
    if (isMobile) toggleSidebar();
  };

  const initials = (user?.name ?? 'CN')
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Sidebar collapsible="icon">
      <ContentLoader show={isLoading} />
      <SidebarHeader className="h-[76px] justify-center gap-2 border-b border-border p-0 px-5">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => handlePush('/')}
              size="lg"
              className="gap-3 px-0 hover:bg-transparent data-[state=open]:bg-transparent"
            >
              <BrandTile />
              <div className="grid flex-1 text-left leading-tight">
                <BrandName className="truncate text-[15px] leading-none" />
                <span className="col-label truncate text-faint">{CONFIG.APP_AUTHOR}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="gap-[22px] overflow-x-auto px-3 py-5">
        {items.map((section) => {
          const accessibleItems = section.items.filter((item) => {
            const resource = item.url === '/' ? 'dashboard' : item.url.split('/')[1]!;
            return hasPermission(resource as keyof typeof ac.statements, 'browse');
          });

          if (accessibleItems.length === 0) return null;

          return (
            <SidebarGroup key={section.title} className="gap-1 p-0">
              <SidebarGroupLabel className="h-auto px-2.5 pb-1.5 text-[10px] tracking-[.16em]">
                {section.title}
              </SidebarGroupLabel>
              <SidebarMenu className="gap-1">
                {accessibleItems.map((item) => {
                  const isActive =
                    pathname === item.url || pathname.startsWith(`${item.url}/`);

                  return (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton
                        onClick={() => handlePush(item.url)}
                        tooltip={item.title}
                        isActive={isActive}
                        className={cn(
                          'relative h-[42px] rounded-[11px] px-3 text-[13.5px] font-semibold',
                          isActive
                            ? 'text-white'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {isValidElement(item.icon)
                          ? cloneElement(item.icon as React.ReactElement<any>, {
                              className: cn(
                                'size-[18px] shrink-0',
                                isActive ? 'text-accent-ink' : 'text-faint',
                              ),
                            })
                          : item.icon}
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          );
        })}
      </SidebarContent>
      <SidebarFooter className="border-t border-border p-3.5">
        <SidebarMenu>
          {/* No celular o topo não comporta o botão: o download vai para a gaveta. */}
          <SidebarMenuItem className="md:hidden">
            <SidebarMenuButton
              asChild
              className="h-[42px] rounded-[11px] px-3 text-[13.5px] font-semibold text-muted-foreground hover:text-foreground"
            >
              <a href={CONFIG.MOBILE_APK_URL} target="_blank" rel="noopener noreferrer">
                <Icon name="download" className="size-[18px] shrink-0 text-faint" />
                <span>Baixar app</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="gap-[11px] rounded-xl border border-border bg-card p-[11px] hover:bg-card data-[state=open]:bg-card"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={user?.image ?? undefined} alt={user?.name} />
                    <AvatarFallback className="rounded-lg">
                      {user?.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left leading-tight">
                    <span className="truncate text-[13px] font-semibold text-white">
                      {user?.name}
                    </span>
                    <span className="truncate text-[11.5px] text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                side={isMobile ? 'bottom' : 'right'}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src={user?.image ?? undefined} alt={user?.name} />
                      <AvatarFallback className="rounded-lg">
                        {user?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{user?.name}</span>
                      <span className="truncate text-xs">{user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => router.push('/profile')}>
                    <User />
                    Meu Perfil
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
